import React from 'react';
import { Paper, Typography, Divider, Grid } from '@mui/material';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import { BASE_CURRENCY, getPaymentMethodLabel, getPaymentMethodLabelWithIcon } from './constants';
import { toCents } from './utils/money';

/**
 * Resumen lateral mostrado debajo del paso de pago. Solo presentación.
 */
const ReservationSummary = ({
  values,
  amount,
  currency,
  showSplitPayments,
  splitPayments,
  getTotalPaid,
  promoCode,
  discountAmount,
  finalAmount
}) => {
  const primaryCustomer = values.customers?.[0];
  const totalPaid = getTotalPaid();

  return (
    <Paper elevation={3} sx={{ p: 3, bgcolor: '#f8f9fa' }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Resumen de la Reserva
      </Typography>
      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <SummaryRow label="Habitación:" value={`${values.roomType} #${values.roomNo}`} />
        <SummaryRow
          label="Huésped Principal:"
          value={primaryCustomer ? `${primaryCustomer.firstName} ${primaryCustomer.lastName}` : ''}
        />

        <SummaryRow
          label="Subtotal:"
          value={`${currency} ${amount}`}
          valueProps={{
            color: 'text.secondary',
            sx: { textDecoration: promoCode ? 'line-through' : 'none' }
          }}
        />

        {promoCode && (
          <SummaryRow
            label={`Descuento (${promoCode.code}):`}
            value={`-${currency} ${discountAmount.toFixed(2)}`}
            labelProps={{ color: 'success.main', fontWeight: 'bold' }}
            valueProps={{ color: 'success.main', fontWeight: 'bold' }}
          />
        )}

        <SummaryRow
          label="Monto Total:"
          value={`${currency} ${finalAmount ? finalAmount.toFixed(2) : amount}`}
          valueProps={{ color: 'primary.main', fontWeight: 'bold' }}
        />

        <SummaryRow
          label="Anticipo:"
          value={values.advanceAmount ? `${currency} ${values.advanceAmount}` : 'No especificado'}
          valueProps={{ color: 'success.main', fontWeight: 'bold' }}
        />

        {showSplitPayments && splitPayments.length > 0 && totalPaid > 0 && (
          <SplitPaymentsBreakdown splitPayments={splitPayments} />
        )}

        {!showSplitPayments && values.paymentMethod && (
          <SummaryRow
            label="Método de Pago:"
            value={getPaymentMethodLabelWithIcon(values.paymentMethod)}
          />
        )}
      </Grid>
    </Paper>
  );
};

const SummaryRow = ({ label, value, labelProps = {}, valueProps = {} }) => (
  <>
    <Grid item xs={6}>
      <Typography variant="body2" color="text.secondary" {...labelProps}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={6}>
      <Typography variant="body2" fontWeight="medium" {...valueProps}>
        {value}
      </Typography>
    </Grid>
  </>
);

const SplitPaymentsBreakdown = ({ splitPayments }) => (
  <>
    <Grid item xs={12}>
      <Divider sx={{ my: 1 }} />
    </Grid>
    <Grid item xs={12}>
      <Typography variant="body2" fontWeight="bold" color="success.main" gutterBottom>
        💰 Métodos de Pago:
      </Typography>
    </Grid>
    {splitPayments
      .filter((p) => toCents(p.amount) > 0)
      .map((payment) => (
        <React.Fragment key={payment.id}>
          <Grid item xs={8}>
            <Typography variant="caption" color="text.secondary">
              • {getPaymentMethodLabel(payment.paymentMethod)} ({payment.currency})
            </Typography>
          </Grid>
          <Grid item xs={4} textAlign="right">
            <Typography variant="caption" fontWeight="medium">
              ${payment.amount}
              {payment.currency !== BASE_CURRENCY && (
                <Typography component="span" variant="caption" color="text.secondary" display="block">
                  ({payment.currency} {parseFloat(payment.amountInForeignCurrency || 0).toFixed(2)})
                </Typography>
              )}
            </Typography>
          </Grid>
        </React.Fragment>
      ))}
  </>
);

export default ReservationSummary;
